/* eslint-disable no-magic-numbers */
import React from 'react';
import { Button } from '@/shared/Button';
import { LoadingIndicator } from '@/shared/LoadingIndicator';
import { Input } from '@/shared/Input';
import { Autocomplete } from '@/shared/Autocomplete';
import { Switch } from '@/shared/Switch/Switch.component';
import { MiriamType } from '@/types/models';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { miramiTypesSelector } from '@/redux/configuration/configuration.selectors';
import { EditElementSection } from '@/components/FunctionalArea/Modal/EditElementModal/EditElementSection.component';
import { useReactionProperties } from '@/components/FunctionalArea/Modal/EditReactionModal/utils/useReactionProperties';
import { ReactionLinesForm } from '@/components/FunctionalArea/Modal/EditReactionModal/ReactionLinesForm.component';

const fieldLabelClass = 'text-xs font-medium text-font-500';
const gridClass = 'grid grid-cols-3 gap-x-3 gap-y-4';

export const ReactionPropertiesForm: React.FC = () => {
  const {
    isSending,
    save,
    name,
    setName,
    notes,
    setNotes,
    reversible,
    isReversibleApplicable,
    handleToggleReversible,
    z,
    annotations,
    handleAnnotationChange,
    handleAnnotationBlur,
    handleAnnotationTypeChange,
    handleAddAnnotation,
    handleRemoveAnnotation,
    handleBringToFront,
    handleSendToBack,
  } = useReactionProperties();

  const miriamTypes = useAppSelector(miramiTypesSelector);
  // A draft (unpersisted) annotation has a negative id; only one is allowed at a time.
  const hasDraftAnnotation = annotations.some(annotation => annotation.id < 0);

  return (
    <div className="flex flex-col gap-6">
      {isSending && (
        <div className="c-edit-element-modal-loader">
          <LoadingIndicator width={44} height={44} />
        </div>
      )}

      <EditElementSection title="Identity">
        <div className={gridClass}>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Name</span>
            <Input
              type="text"
              name="name"
              id="name"
              data-testid="reaction-name"
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={() => save()}
              sizeVariant="medium"
              className="text-xs"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Description</span>
            <Input
              type="text"
              name="notes"
              id="notes"
              data-testid="reaction-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={() => save()}
              sizeVariant="medium"
              className="text-xs"
            />
          </div>
          {isReversibleApplicable && (
            <div className="flex flex-col gap-1">
              <span className={fieldLabelClass}>Reversible</span>
              <Switch
                isChecked={reversible}
                onToggle={handleToggleReversible}
                data-testid="reaction-reversible-switch"
              />
            </div>
          )}
        </div>
      </EditElementSection>

      <EditElementSection title="Annotations">
        <div className="flex flex-col gap-2">
          {annotations.map((annotation, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="grid grid-cols-[1fr_1fr_auto] items-center gap-2"
            >
              <Autocomplete<MiriamType>
                options={miriamTypes}
                initialValue={miriamTypes.find(type => type.name === annotation.type) || null}
                valueKey="name"
                labelKey="commonName"
                placeholder="Type"
                onChange={selected => handleAnnotationTypeChange(index, selected?.name ?? '')}
              />
              <Input
                type="text"
                placeholder="Resource"
                value={annotation.resource}
                onChange={e => handleAnnotationChange(index, 'resource', e.target.value)}
                onBlur={() => handleAnnotationBlur(index)}
                className="text-xs"
              />
              <Button type="button" onClick={() => handleRemoveAnnotation(index)}>
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            className="w-fit"
            disabled={hasDraftAnnotation}
            onClick={handleAddAnnotation}
          >
            Add annotation
          </Button>
        </div>
      </EditElementSection>

      {/*
        Rendered here rather than by the modal so that the sections appear in the
        agreed order (identity, annotations, line, stacking order) - this form owns
        the only instance of useReactionProperties, whose state backs every save.
      */}
      <ReactionLinesForm />

      <EditElementSection title="Stacking order" defaultExpanded={false}>
        <div className="flex items-center gap-2">
          <span className={fieldLabelClass}>z-index: {z}</span>
          <Button type="button" onClick={handleBringToFront}>
            Bring to front
          </Button>
          <Button type="button" onClick={handleSendToBack}>
            Send to back
          </Button>
        </div>
      </EditElementSection>
    </div>
  );
};
